import { useState, useCallback, memo, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWidgetSettings } from "../WidgetSettings";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapMarkerAlt,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const MapConfigForm = memo(function MapConfigForm({ config, onUpdate }) {
  const { setActionButton, clearActionButton } = useWidgetSettings();

  const [formData, setFormData] = useState({
    latitude: config.latitude || null,
    longitude: config.longitude || null,
    zoom: config.zoom || 10,
    style: config.style || "light-v10",
    showMarker: config.showMarker !== false,
    markerColor: config.markerColor || "#4C05FF",
    address: config.address || "",
    showLabel: config.showLabel !== false,
    labelText: config.labelText || "",
    isDarkMode: config.isDarkMode ?? false, // Default to light mode
  });

  const [addressQuery, setAddressQuery] = useState(config.address || "");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState("");
  const searchTimeout = useRef(null);
  const resultsRef = useRef(null);

  // Predefined marker colors using Tailwind theme colors
  const markerColors = [
    { value: "#4C05FF", label: "Primary" }, // primary.DEFAULT
    { value: "#60A5FA", label: "Blue" }, // accent.blue
    { value: "#2DD4BF", label: "Teal" }, // accent.teal
    { value: "#D4FF00", label: "Lime" }, // accent.lime
    { value: "#FF6B6B", label: "Coral" }, // accent.coral
    { value: "#EC7156", label: "Coral Orange" }, // accent.coralog
    { value: "#FFFFFF", label: "White" },
    { value: "#000000", label: "Black" },
  ];

  // Intelligent zoom levels based on location type
  const getZoomLevel = (feature) => {
    const placeType = feature.place_type?.[0] || feature.properties?.category;

    if (placeType === "country") return 5;
    if (placeType === "region" || placeType === "state") return 7;
    if (placeType === "district" || placeType === "county") return 9;
    if (placeType === "place" || placeType === "locality") return 11;
    if (placeType === "neighborhood") return 13;
    if (placeType === "address" || placeType === "poi") return 15;

    // Default zoom based on feature context
    const context = feature.context || [];
    if (context.some((c) => c.id.startsWith("country"))) {
      if (context.some((c) => c.id.startsWith("address"))) return 15;
      if (context.some((c) => c.id.startsWith("postcode"))) return 13;
      if (context.some((c) => c.id.startsWith("place"))) return 11;
      if (context.some((c) => c.id.startsWith("region"))) return 9;
    }

    return 12; // Default zoom
  };

  // Search for addresses using Mapbox Geocoding API
  const searchAddresses = useCallback(async (query) => {
    if (!query.trim() || query.length < 3) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setSearchError("");

    try {
      const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      if (!token) {
        throw new Error("Mapbox token not configured");
      }

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${token}&limit=5&types=country,region,district,place,locality,neighborhood,address,poi`
      );

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        setSearchResults(data.features);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
        setSearchError("No locations found. Try a different search term.");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      setSearchError("Failed to search locations. Please try again.");
      setSearchResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle address input with debounced search
  const handleAddressChange = useCallback(
    (value) => {
      setAddressQuery(value);
      setSearchError("");

      // Clear existing timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      // Debounce search
      if (value.trim().length >= 3) {
        searchTimeout.current = setTimeout(() => {
          searchAddresses(value);
        }, 300);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    },
    [searchAddresses]
  );

  // Handle selecting a search result
  const handleSelectLocation = useCallback(
    (feature) => {
      const [longitude, latitude] = feature.center;
      const placeName = feature.place_name || feature.text;
      const zoom = getZoomLevel(feature);

      const updatedFormData = {
        ...formData,
        latitude,
        longitude,
        zoom,
        address: placeName,
        labelText: placeName,
      };

      setFormData(updatedFormData);
      setAddressQuery(placeName);
      setShowResults(false);
      setSearchResults([]);

      // Auto-save immediately when location is selected
      onUpdate(updatedFormData);
    },
    [formData, onUpdate]
  );

  // Handle marker color change
  const handleMarkerColorChange = useCallback(
    (color) => {
      const updatedFormData = {
        ...formData,
        markerColor: color,
      };

      setFormData(updatedFormData);
      // Auto-save immediately when marker color is changed
      onUpdate(updatedFormData);
    },
    [formData, onUpdate]
  );

  // Handle clicks outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  // Clear action button since we auto-save
  useEffect(() => {
    clearActionButton();
  }, [clearActionButton]);

  return (
    <div className="space-y-6">
      {/* Address Search */}
      <div className="space-y-2 relative" ref={resultsRef}>
        <Label className="text-white">Location</Label>
        <div className="relative">
          <Input
            value={addressQuery}
            onChange={(e) => handleAddressChange(e.target.value)}
            placeholder="Enter city, address, or zipcode"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/50 pr-10"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className="w-4 h-4 text-white/50 animate-spin"
              />
            ) : (
              <FontAwesomeIcon
                icon={faSearch}
                className="w-4 h-4 text-white/50"
              />
            )}
          </div>
        </div>

        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((feature, index) => (
              <button
                key={index}
                onClick={() => handleSelectLocation(feature)}
                className="w-full text-left px-4 py-3 hover:bg-gray-800 transition-colors border-b border-gray-700 last:border-b-0"
              >
                <div className="flex items-start gap-3">
                  <FontAwesomeIcon
                    icon={faMapMarkerAlt}
                    className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0"
                  />
                  <div>
                    <div className="text-white text-sm font-medium">
                      {feature.text}
                    </div>
                    <div className="text-gray-400 text-xs">
                      {feature.place_name}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Search Error */}
        {searchError && (
          <p className="text-red-400 text-xs mt-1">{searchError}</p>
        )}

        {/* Current Address Display */}
        {formData.address && (
          <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0"
              />
              <div>
                <div className="text-blue-400 text-sm font-medium">
                  Current Location
                </div>
                <div className="text-white text-xs">{formData.address}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Marker Color */}
      <div className="space-y-3">
        <Label className="text-white">Marker color</Label>
        <div className="flex gap-2 flex-wrap">
          {markerColors.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => handleMarkerColorChange(color.value)}
              className={`w-6 h-6 rounded-full border-2 transition-all ${
                formData.markerColor === color.value
                  ? "border-white scale-110 shadow-lg"
                  : "border-white/30 hover:border-white/60 hover:scale-105"
              }`}
              style={{
                backgroundColor: color.value,
                boxShadow:
                  formData.markerColor === color.value
                    ? `0 0 0 2px ${color.value}40`
                    : "none",
              }}
              title={color.label}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

MapConfigForm.displayName = "MapConfigForm";

export default MapConfigForm;
